import { CUMULATIVE_SIZE_CURRENCY, CategorisedLiquidity } from '@drift/common';
import { cj, cm } from 'src/utils/classNames';
import { ORDERBOOK_DISPLAY_TYPE } from '../useOrderbookDisplayPreference';
import { useRef } from 'react';
import { getLiquidityDisplayBgStyle } from '../OrderbookUtils';
import { OrderbookRowPrice } from './OrderbookRowPrice';
import { OrderbookRowSize } from './OrderbookRowSize';
import { OrderbookRowCumulativeSize } from './OrderbookRowCumulativeSize';
import { ORDERBOOK_ROW_GAP_DESKTOP, ROW_HEIGHT } from '../OrderbookConstants';
import { OrderbookRowHoverContent } from '../OrderbookRowHoverContent';

const ORDERBOOK_ROW_TOP_BORDER_HOVER_STYLE =
	'group-hover:after:content-[""] group-hover:after:absolute group-hover:after:left-0 group-hover:after:right-0 group-hover:after:top-0 group-hover:after:border-t'; // This style applies a top border to the orderbook row when hovered without affecting the layout of the content

const ORDERBOOK_ROW_BOTTOM_BORDER_HOVER_STYLE =
	'group-hover:after:content-[""] group-hover:after:absolute group-hover:after:left-0 group-hover:after:right-0 group-hover:after:bottom-0 group-hover:after:border-b'; // This style applies a bottom border to the orderbook row when hovered without affecting the layout of the content

const ORDERBOOK_ROW_NEGATIVE_RED_BORDER =
	'group-hover:after:border-text-negative-red-button';

const ORDERBOOK_ROW_POSITIVE_GREEN_BORDER =
	'group-hover:after:border-text-positive-green-button';

export const EmptyOrderbookRow = () => {
	return <div className="w-full" style={{ height: ROW_HEIGHT + 'px' }} />;
};

const LiquidityBackgroundDisplay = ({
	cumulativeSize,
	totalSize,
	displayType,
	side,
}: {
	cumulativeSize: CategorisedLiquidity;
	totalSize: number;
	displayType: ORDERBOOK_DISPLAY_TYPE;
	side: 'buy' | 'sell';
}) => {
	const totalCumulativeSize = Object.values(cumulativeSize).reduce(
		(acc, curr) => acc + curr,
		0
	);

	const cumulativeSizePct = totalSize
		? (totalCumulativeSize / totalSize) * 100
		: 0; // This is the portion of the total size of the orderbook that the cumulative size of the liquidity should be occupying.

	const cumulativeRestingSize =
		totalCumulativeSize - (cumulativeSize?.indicative ?? 0);
	const cumulativeIndicativeSize = cumulativeSize?.indicative ?? 0;

	const restingLiquidityPct =
		(cumulativeRestingSize / totalCumulativeSize) * cumulativeSizePct; // This is the portion of the width of the orderbook that the resting liquidity should be occupying

	const indicativeLiquidityPct =
		(cumulativeIndicativeSize / totalCumulativeSize) * cumulativeSizePct; // This is the portion of the width of the orderbook that the indicative liquidity should be occupying

	const invertBgPosition =
		displayType === ORDERBOOK_DISPLAY_TYPE.HORIZONTAL && side === 'buy';

	const roundedClassName = invertBgPosition ? 'rounded-l-xs' : 'rounded-r-xs';

	return (
		<div
			className={cj(
				'pointer-events-none absolute flex h-full w-full group-hover:brightness-125',
				invertBgPosition ? 'flex-row-reverse' : 'justify-start'
			)}
		>
			<div
				className={cj(
					'h-full transition-[width] duration-300 ease-out',
					roundedClassName
				)}
				style={{
					width: `${restingLiquidityPct + indicativeLiquidityPct}%`,
					...getLiquidityDisplayBgStyle('dlob', side),
				}}
			/>
		</div>
	);
};

export const OrderbookRow = ({
	side,
	price,
	rowSize,
	cumulativeSize,
	cumulativeAvgPrice,
	totalSize,
	zeroPadding = 0,
	isCurrentUserLiquidity,
	currentUserLiquiditySize: _currentUserLiquiditySize,
	displayType,
	baseDisplayDecimals,
	isMobile,
	otherSideWrapperBoundingClientRect,
	rowsSectionWrapperBoundingClientRect,
	cumulativeSizeCurrency,
	isMini,
}: {
	side: 'buy' | 'sell';
	price: number;
	rowSize: CategorisedLiquidity;
	cumulativeSize: CategorisedLiquidity;
	cumulativeAvgPrice: number;
	totalSize: number;
	zeroPadding?: number;
	isCurrentUserLiquidity?: boolean;
	currentUserLiquiditySize?: number;
	displayType: ORDERBOOK_DISPLAY_TYPE;
	baseDisplayDecimals: number;
	isMobile: boolean;
	otherSideWrapperBoundingClientRect: DOMRect | null;
	rowsSectionWrapperBoundingClientRect: DOMRect | null;
	cumulativeSizeCurrency: CUMULATIVE_SIZE_CURRENCY;
	isMini?: boolean;
}) => {
	const cumulativeTotalSize = Object.values(cumulativeSize).reduce(
		(acc, curr) => acc + curr,
		0
	);

	const isHorizontalBuySideOrderbook =
		displayType === ORDERBOOK_DISPLAY_TYPE.HORIZONTAL && side === 'buy';
	const invertNumberPositions = isHorizontalBuySideOrderbook;

	const orderbookRowRef = useRef<HTMLDivElement>(null);

	const OrderbookRowContent = (
		<>
			<div
				className={cj(
					'relative flex w-full justify-between',
					'typo-b4 text-text-default'
				)}
				style={{
					height: `${ROW_HEIGHT}px`,
				}}
				ref={orderbookRowRef}
			>
				<div
					className={cm(
						`flex w-full justify-between px-2`,
						invertNumberPositions && 'flex-row-reverse',
						side === 'buy'
							? ORDERBOOK_ROW_POSITIVE_GREEN_BORDER
							: ORDERBOOK_ROW_NEGATIVE_RED_BORDER,
						// If we are horizontal, render the hover border on bottom.
						!isMobile && displayType === ORDERBOOK_DISPLAY_TYPE.HORIZONTAL
							? ORDERBOOK_ROW_BOTTOM_BORDER_HOVER_STYLE
							: // Else, render it on bottom for buy rows / top for sell rows.
								side === 'buy'
								? ORDERBOOK_ROW_BOTTOM_BORDER_HOVER_STYLE
								: ORDERBOOK_ROW_TOP_BORDER_HOVER_STYLE,
						!isMobile && 'group-hover:bg-button-disabled'
					)}
				>
					{displayType === ORDERBOOK_DISPLAY_TYPE.VERTICAL ? (
						<>
							<OrderbookRowPrice
								price={price}
								side={side}
								zeroPadding={zeroPadding}
								className="flex-1"
								displayType={displayType}
								isCurrentUserLiquidity={isCurrentUserLiquidity}
							/>
							<OrderbookRowSize
								rowSize={rowSize}
								baseDisplayDecimals={baseDisplayDecimals}
								isMobile={isMobile}
								className="flex-2 justify-end"
								currency={cumulativeSizeCurrency}
								price={price}
							/>
							{!isMini && (
								<OrderbookRowCumulativeSize
									cumulativeSize={cumulativeSize}
									baseDisplayDecimals={baseDisplayDecimals}
									isMobile={isMobile}
									className={'flex-2'}
									currency={cumulativeSizeCurrency}
									cumulativeAveragePrice={cumulativeAvgPrice}
								/>
							)}
						</>
					) : (
						<>
							<OrderbookRowPrice
								price={price}
								side={side}
								zeroPadding={zeroPadding}
								className={cj(
									'flex-1',
									isHorizontalBuySideOrderbook ? 'justify-end' : 'justify-start'
								)}
								displayType={displayType}
								isCurrentUserLiquidity={isCurrentUserLiquidity}
							/>
							<OrderbookRowSize
								rowSize={rowSize}
								baseDisplayDecimals={baseDisplayDecimals}
								isMobile={isMobile}
								className={cj(
									'flex-1',
									isHorizontalBuySideOrderbook ? 'justify-start' : 'justify-end'
								)}
								currency={cumulativeSizeCurrency}
								price={price}
							/>
						</>
					)}
				</div>
				<LiquidityBackgroundDisplay
					cumulativeSize={cumulativeSize}
					totalSize={totalSize}
					displayType={displayType}
					side={side}
				/>
				{!isMobile && (
					<OrderbookRowHoverContent
						cumulativeAvgPrice={cumulativeAvgPrice}
						cumulativeTotalSize={cumulativeTotalSize}
						cumulativeLiquidity={cumulativeSize}
						side={side}
						displayType={displayType}
						baseDisplayDecimals={baseDisplayDecimals}
						otherSideWrapperBoundingClientRect={
							otherSideWrapperBoundingClientRect
						}
						rowsSectionWrapperBoundingClientRect={
							rowsSectionWrapperBoundingClientRect
						}
						currency={cumulativeSizeCurrency}
					/>
				)}
			</div>
		</>
	);

	// We show a tooltip on row hover; however, due to having gaps between rows,
	// when a user moves over multiple rows in succession, there is a flicker
	// effect of the tooltip quickly showing / hiding (because it hides when hover
	// is on a gap / between rows). So to prevent this, we extend the hover area
	// of each row with some padding / negative margin.
	const style = (() => {
		if (isMobile) {
			return;
		}
		const shouldAddToTop =
			displayType === ORDERBOOK_DISPLAY_TYPE.VERTICAL && side === 'sell';

		const padding = shouldAddToTop ? 'paddingTop' : 'paddingBottom';
		const margin = shouldAddToTop ? 'marginTop' : 'marginBottom';

		return {
			[padding]: `${ORDERBOOK_ROW_GAP_DESKTOP}px`,
			[margin]: `-${ORDERBOOK_ROW_GAP_DESKTOP}px`,
		};
	})();

	return (
		<div
			className={cm(
				'relative w-full',
				!isMobile && 'group [&:hover~*]:bg-button-disabled'
			)}
			style={style}
		>
			{OrderbookRowContent}
		</div>
	);
};
